import { createFileRoute, Navigate, Outlet } from "@tanstack/react-router";
import { AppContainer } from "@bfc-software/pallet";
import { useContext } from "react";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
  HYDRATING,
  CREATING_SESSION,
} from "@bfc-software/app-toolbox";

export const Route = createFileRoute("/$lang/_layout/orgs/$org/_layout")({
  component: RouteComponent,
});

export function RouteComponent() {
  const params = Route.useParams();
  const { state } = useContext(AuthContext);

  // Transient states that already hold a valid token - sit tight rather than
  // treating them as signed out. CREATING_SESSION is reached on every
  // warehouse switch, and bouncing to login there sent the user to the
  // product picker by way of a login screen that immediately redirected them
  // back out, losing the page they were on. A warehouse switch keeps the
  // current page mounted rather than blanking it, since the session is being
  // re-minted underneath, not established.
  if (state.status === HYDRATING) return <></>;
  if (state.status === CREATING_SESSION) {
    return (
      <AppContainer>
        <Outlet />
      </AppContainer>
    );
  }

  // Signed out, including after a 401 middleware's logout().
  if (state.status !== AUTHENTICATED) {
    return (
      <Navigate to="/$lang/auth/login" params={{ lang: params.lang }} replace />
    );
  }

  // The session org is the only org this session can act in (the auth machine has no
  // post-login org switch). It is also where every redirect below lands.
  const sessionOrg = AuthUtils.getOrgById(state, state.orgId);
  if (!sessionOrg || typeof sessionOrg?.org_slug !== "string") {
    return (
      <Navigate to="/$lang/auth/login" params={{ lang: params.lang }} replace />
    );
  }

  const routeOrg = AuthUtils.getOrgs(state).find(
    (org) => org.org_slug === params.org || org.org_id === params.org,
  );

  // (a) Unknown org, or one of the user's *other* orgs: the picker of the session org.
  // A warehouse is deliberately not required to get this far: the tile screen has no
  // warehouse, and tools like analytics span them.
  if (!routeOrg || routeOrg.org_id !== state.orgId) {
    return (
      <Navigate
        to="/$lang/orgs/$org"
        params={{ lang: params.lang, org: sessionOrg.org_slug }}
        replace
      />
    );
  }

  // (b) The URL names the session org by id: same location, `$org` = slug, so everything
  // below only ever sees slugs. No `from`: that resolves to the deepest match, so deeper
  // segments (`/outbound/whs/W1/tasks/plans`) and their params survive; `true` keeps search + hash.
  if (params.org !== sessionOrg.org_slug) {
    return (
      <Navigate
        to="."
        params={(prev) => ({ ...prev, org: sessionOrg.org_slug })}
        search
        hash
        replace
      />
    );
  }

  return (
    <AppContainer>
      <Outlet />
    </AppContainer>
  );
}
